'use client'

import { memo, useEffect, useRef } from 'react'
import { Maximize2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

type LandingDemoVideoLightboxProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  demoSrc: string
  posterSrc?: string | null
  /** baby: phone demos stay tall; desktop demos stay wide */
  aspect?: 'video' | 'phone'
}

export const LandingDemoVideoLightbox = memo(function LandingDemoVideoLightbox({
  open,
  onOpenChange,
  title,
  demoSrc,
  posterSrc,
  aspect = 'video',
}: LandingDemoVideoLightboxProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  // baby: autoplay when opened; pause when closed so audio/cpu stop
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (open) {
      video.currentTime = 0
      void video.play().catch(() => undefined)
    } else {
      video.pause()
    }
  }, [open, demoSrc])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-h-[92vh] w-[min(96vw,1100px)] max-w-none gap-0 overflow-hidden border-0 bg-black p-0 text-white shadow-2xl sm:rounded-xl',
          // baby: keep close control readable on dark video chrome
          '[&>button]:right-3 [&>button]:top-3 [&>button]:z-20 [&>button]:rounded-full [&>button]:bg-black/50 [&>button]:p-2 [&>button]:text-white [&>button]:opacity-100 [&>button]:hover:bg-black/70 [&>button]:hover:opacity-100',
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          Expanded demo video. Tap outside or press Escape to close.
        </DialogDescription>
        <div
          className={cn(
            'relative mx-auto w-full bg-black',
            aspect === 'phone'
              ? 'aspect-[9/16] max-h-[88vh] max-w-[420px]'
              : 'aspect-video max-h-[88vh]',
          )}
        >
          <video
            ref={videoRef}
            key={demoSrc}
            className="absolute inset-0 h-full w-full object-contain"
            src={demoSrc}
            poster={posterSrc ?? undefined}
            controls
            playsInline
            autoPlay
            muted
            loop
          />
        </div>
        <p className="truncate px-4 py-3 text-sm text-white/80">{title}</p>
      </DialogContent>
    </Dialog>
  )
})

type ExpandDemoVideoButtonProps = {
  onClick: () => void
  className?: string
}

/** baby: sits in the same control row as play / pause / skip */
export const ExpandDemoVideoButton = memo(function ExpandDemoVideoButton({
  onClick,
  className,
}: ExpandDemoVideoButtonProps) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/20 text-white backdrop-blur-sm transition-all hover:scale-110 hover:bg-white/30',
        className,
      )}
      aria-label="Expand video"
      title="Expand video"
    >
      <Maximize2 className="h-5 w-5" />
    </button>
  )
})
